import React from 'react';
import { useTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';
import { BiData, GalleryPurchaseBiEvent, BiContextProvidedValue } from '@wix/da-bi/pkg/events';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import type { PapiDeviation } from '@wix/da-papi-types';
import ThumbMiddleContent from '@wix/da-ds/pkg/thumbs/legos/ThumbMiddleContent';
import PointsIcon from '@wix/da-ds/pkg/Icons/Points';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import ThumbStatusIndicator from '@wix/da-ds/pkg/thumbs/ThumbStatusIndicator';
import HoverOnly from '@wix/da-ds/pkg/thumbs/OverlayHelpers/HoverOnly';
import { useRestrictToLoggedIn } from '../../../../../../utils/hooks/useRestrictToLoggedIn';
import { LoggedOutReturnType } from '../../../../../../redux/loggedout';
import { normalizePremiumFolderData } from '../../../../../../utils/normalizePremiumFolderData';
import { getDeviationResourceType } from '../../../../../_helpers';

interface Props {
  deviation: PapiDeviation;
  thumbWidth?: number;
  thumbHeight?: number;
  className?: string;
}

const PremiumLockedMiddleContent: React.FC<Props> = ({
  deviation,
  thumbWidth,
  thumbHeight,
  className,
}) => {
  const { t } = useTranslation();
  const { restrictToLoggedIn } = useRestrictToLoggedIn();
  const { pointPrice, folderId, subproductId, formattedDollarPrice } = normalizePremiumFolderData(
    deviation.premiumFolderData
  );

  const isUnreasonablySmall =
    (thumbHeight && thumbHeight < 160) || (thumbWidth && thumbWidth < 120);

  if (isUnreasonablySmall) {
    return (
      <HoverOnly reverseHidden>
        <ThumbMiddleContent
          icon={<ThumbStatusIndicator type="premium-folder" size="large" />}
          className={className}
        />
      </HoverOnly>
    );
  }

  const checkoutLink = subproductId ? Url.checkoutLink(subproductId) : undefined;

  return (
    <ThumbMiddleContent
      icon={<ThumbStatusIndicator type="premium-folder" size="large" />}
      subtitle={t('deviationview.overlay.premiumFolder.subtitle', {
        postProcess: ['reactPostprocessor', 'featureBrancher'],
        dollarPrice: formattedDollarPrice,
        pointPrice,
        pointsIcon: <PointsIcon size={IconSize.TINY} disableDefaultColors />,
      })}
      buttonTitle={t('deviationview.overlay.premiumFolder.button.pay.simple')}
      biData={BiData<GalleryPurchaseBiEvent>({
        evid: 361,
        typeid: getDeviationResourceType(deviation),
        itemid: deviation.deviationId,
        authorid: deviation.author?.useridUuid,
        galleryid: folderId ?? 0,
        points: pointPrice,
        rank: BiContextProvidedValue,
        reason_shown: BiContextProvidedValue,
        section_typeid: BiContextProvidedValue,
        sectionid: BiContextProvidedValue,
        sectionname: BiContextProvidedValue,
        sectionrank: BiContextProvidedValue,
        sectiontype: BiContextProvidedValue,
        sort_type: BiContextProvidedValue,
        widgetname: BiContextProvidedValue,
      })}
      onButtonClick={restrictToLoggedIn(
        ReferralType.ACTION_BAR,
        () => {
          if (checkoutLink) window.location.href = checkoutLink;
        },
        checkoutLink,
        LoggedOutReturnType.JOIN_ONLY
      )}
      className={className}
    />
  );
};
PremiumLockedMiddleContent.displayName = 'PremiumLockedMiddleContent';

export default PremiumLockedMiddleContent;
